/**
 * Section Registry — maps section types to render functions.
 *
 * Each entry provides:
 *   - default:  (props) => ReactNode — base renderer
 *   - variants: { [name]: (props) => ReactNode } — layout-specific renderers
 *   - wrapper:  (children, context) => ReactNode — optional outer wrapper
 *
 * Render props received by each renderer:
 *   { mainDraw, bonusDraws, partner, loading, editMode, sections,
 *     updateSection, salesClosed, t, onLoginClick, gameType,
 *     variant, cellConfig, isAdmin }
 *
 * Phase 0: Classic 50/50 sections only. Other game types added in Phase 1.
 */

import React, { lazy, Suspense } from 'react'
import { formatCurrencyValue } from './formatCurrency'

// Shared components (already standalone)
import BonusDrawsList from '../components/sections/BonusDrawsList'
import CompactWinnersList from '../components/CompactWinnersList'
import Leaderboard from '../components/Leaderboard'
import LiveDrawCard from '../components/LiveDrawCard'
import PartnerBanner from '../components/PartnerBanner'
import WinnersBanner from '../components/WinnersBanner'
import RecentWinners from '../components/RecentWinners'
import RichImageCarousel from '../components/RichImageCarousel'
import OrganizationWriteupSection from '../components/OrganizationWriteupSection'
import SpacerSection from '../components/SpacerSection'
import SquareGridSection from '../components/sections/SquareGridSection'
import GoalThermometerSection from '../components/sections/GoalThermometerSection'
import { SplitPotCharity } from '../components/charity'
import { CharitySidebar } from '../components/CharityHeroSection'
import CountdownTimerStyles from '../components/CountdownTimerStyles'
import { useCountdown } from '../hooks/useCountdown'

// ---------------------------------------------------------------------------
// Section registry
// ---------------------------------------------------------------------------

const registry = {}

/**
 * Register a section type with its renderers.
 */
function register(type, config) {
  registry[type] = {
    default: config.default || (() => null),
    variants: config.variants || {},
    wrapper: config.wrapper || null,
  }
}

/**
 * Look up and render a section. Falls back through:
 *   1. Requested variant
 *   2. Default renderer
 *   3. null (unknown section)
 */
export function renderSection(type, props = {}) {
  const entry = registry[type]
  if (!entry) return null

  const { variant = 'default', ...rest } = props
  const renderer = entry.variants?.[variant] || entry.default
  if (!renderer) return null

  let content = renderer(rest)
  if (content && entry.wrapper) {
    content = entry.wrapper(content, rest)
  }
  return content
}

/**
 * Check if a section type is registered.
 */
export function hasSectionRenderer(type) {
  return type in registry
}

/**
 * Get all registered section types.
 */
export function getRegisteredTypes() {
  return Object.keys(registry)
}

// ---------------------------------------------------------------------------
// Section registrations (all game types)
// ---------------------------------------------------------------------------

// --- Image Slider ---
register('image_slider', {
  default: ({ slides }) => {
    if (!slides || slides.length === 0) return null
    return <RichImageCarousel slides={slides} />
  },
})

// --- Goal Thermometer ---
register('goal_thermometer', {
  default: ({ mainDraw, isAdmin, editMode, refetch }) => (
    <GoalThermometerSection
      mainDraw={mainDraw}
      isAdmin={isAdmin}
      editMode={editMode}
      onGoalSaved={refetch}
    />
  ),
})

// --- Organization Writeup ---
register('organization_writeup', {
  default: ({ mainDraw, gameType, slides, editMode, handleTranslationUpdate, language }) => (
    <OrganizationWriteupSection
      mainDraw={mainDraw}
      gameType={gameType}
      slides={slides}
      editMode={editMode}
      handleTranslationUpdate={handleTranslationUpdate}
    />
  ),
})

register('organization_writeup_2', {
  default: ({ mainDraw, gameType, editMode, handleTranslationUpdate }) => (
    <OrganizationWriteupSection
      mainDraw={mainDraw}
      gameType={gameType}
      editMode={editMode}
      handleTranslationUpdate={handleTranslationUpdate}
      showSlides={false}
    />
  ),
})

// --- Bonus Draws ---
register('bonus_draws', {
  default: ({ bonusDraws, editMode }) => {
    return <BonusDrawsList draws={bonusDraws} editMode={editMode} />
  },
  variants: {
    compact: ({ bonusDraws, editMode }) => {
      return <BonusDrawsList draws={bonusDraws} editMode={editMode} compact />
    },
  },
})

// --- Compact Winners ---
register('compact_winners', {
  default: ({ editMode }) => {
    return <CompactWinnersList editMode={editMode} />
  },
})

// --- Recent Winners ---
register('recent_winners', {
  default: ({ editMode }) => {
    return (
      <div className="py-8 px-4">
        <RecentWinners editMode={editMode} />
      </div>
    )
  },
})

// --- Leaderboard ---
register('leaderboard', {
  default: ({ editMode, sections, updateSection }) => {
    const section = sections?.find(s => s.section_type === 'leaderboard')
    return (
      <div className="mt-8">
        <Leaderboard
          editMode={editMode}
          period={section?.config?.period || 'currentDraw'}
          limit={section?.config?.limit || 10}
          showBadges={section?.config?.showBadges !== false}
          showPlacement={(section?.config?.showPlacement ?? section?.config?.showBadges) !== false}
          showSupporters={section?.config?.showSupporters !== false}
          showTickets={section?.config?.showTickets !== false}
          showAmount={section?.config?.showAmount !== false}
          displayStyle={section?.config?.displayStyle || 'list'}
          sortBy={section?.config?.sortBy || 'amount'}
          section={section}
          updateSection={updateSection}
        />
      </div>
    )
  },
})

// --- Live Draw Card ---
register('live_draw_card', {
  default: ({ mainDraw, editMode, language }) => {
    return <LiveDrawCard draw={mainDraw} editMode={editMode} hideCountdown />
  },
})

// --- Partner Banner ---
register('partner_banner', {
  default: ({ partner }) => {
    if (!partner) return null
    return (
      <div className="py-8 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <PartnerBanner partner={partner} />
        </div>
      </div>
    )
  },
})

// --- Winners Banner ---
register('winners_banner', {
  default: ({ editMode, gameType, sections }) => {
    const section = sections?.find(s => s.section_type === 'winners_banner')
    return (
      <div className="py-8 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <WinnersBanner
            editMode={editMode}
            gameType={gameType}
            maxWinners={section?.config?.maxWinners || 3}
          />
        </div>
      </div>
    )
  },
})

// --- Charity Banner ---
register('charity_banner', {
  default: () => {
    return (
      <div className="py-8 bg-white dark:bg-gray-800">
        <div className="max-w-md mx-auto px-4">
          <SplitPotCharity />
        </div>
      </div>
    )
  },
})

// --- Charity Impact ---
register('charity_impact', {
  default: () => {
    return (
      <div className="py-12 bg-gray-50 dark:bg-gray-800/50">
        <div className="max-w-7xl mx-auto px-4">
          <SplitPotCharity />
        </div>
      </div>
    )
  },
})

// --- Charity Sidebar ---
register('charity_sidebar', {
  default: ({ sections }) => {
    const section = sections?.find(s => s.section_type === 'charity_sidebar')
    return <CharitySidebar charityConfig={section?.config || {}} />
  },
})

// --- Spacers ---
register('spacer_1', {
  default: ({ sections }) => <SpacerSection sectionType="spacer_1" sections={sections} />,
})
register('spacer_2', {
  default: ({ sections }) => <SpacerSection sectionType="spacer_2" sections={sections} />,
})
register('spacer_3', {
  default: ({ sections }) => <SpacerSection sectionType="spacer_3" sections={sections} />,
})

// --- Content Blocks (colored text banners) ---
import ContentBlockSection from '../components/ContentBlockSection'

register('content_block_1', {
  default: () => <ContentBlockSection sectionType="content_block_1" />,
})
register('content_block_2', {
  default: () => <ContentBlockSection sectionType="content_block_2" />,
})
register('content_block_3', {
  default: () => <ContentBlockSection sectionType="content_block_3" />,
})

// --- Final CTA ---
register('final_cta', {
  default: ({ mainDraw, salesClosed, onLoginClick, t, language }) => {
    if (!mainDraw) return null
    return (
      <div className="py-16 bg-gradient-to-br from-blue-600 to-purple-700 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            {t?.('progMomentum.finalCtaTitle') || 'Don\'t Miss Out!'}
          </h2>
          <p className="text-lg text-white/80 mb-8">
            {t?.('progMomentum.finalCtaDesc') || 'Get your tickets before it\'s too late'}
          </p>
          {!salesClosed && (
            <button
              onClick={onLoginClick}
              className="bg-white text-blue-700 px-8 py-3 rounded-lg font-bold text-lg hover:bg-blue-50 transition-colors"
            >
              {t?.('progMomentum.finalCtaButton') || 'Buy Tickets Now'}
            </button>
          )}
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Hero Section — the most complex, with multiple variants per layout
// ---------------------------------------------------------------------------

register('hero_section', {
  // Standard: operated-by text + partner name + title + description
  // Matches Classic5050StyleStandard hero for consistency across grid layouts
  default: ({ mainDraw, partner, t, editMode, language }) => {
    if (!mainDraw) return null
    return (
      <div className="mb-8 text-center">
        <p className="text-sm text-gray-600 dark:text-gray-400 font-medium tracking-wide uppercase mb-6">
          {t?.('standard.operatedBy') || 'Operated by The Lotto Factory'}
        </p>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
          {partner?.partner_name ? (
            `${partner.partner_name.toUpperCase()} ${mainDraw.title?.toUpperCase() || t?.('display.jackpot') || 'JACKPOT'}`
          ) : (
            mainDraw.title?.toUpperCase() || mainDraw.name || t?.('display.jackpot') || 'JACKPOT'
          )}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
          {t?.('standard.subtitle') || "By participating in this fundraiser you're directly supporting our community programs and services."}
        </p>
      </div>
    )
  },
  variants: {
    // PremiumPlus hero: gradient background, 2-col with jackpot card
    'premium-plus': ({ mainDraw, salesClosed, onLoginClick, t, editMode, language }) => {
      // Placeholder — full PremiumPlus hero will be extracted in template conversion
      if (!mainDraw) return null
      return (
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-900 via-blue-800 to-purple-900 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-4xl lg:text-5xl font-bold">
                {mainDraw.name || t?.('sectionRegistry.premiumDraw') || 'Premium+ Draw'}
              </h1>
              <p className="mt-4 text-lg text-white/80">
                {mainDraw.subtitle || t?.('standard.subtitle') || ''}
              </p>
              {!salesClosed && (
                <button
                  onClick={onLoginClick}
                  className="mt-6 bg-white text-blue-800 px-8 py-3 rounded-lg font-bold hover:bg-blue-50 transition-colors"
                >
                  {t?.('standard.buyTickets') || 'Buy Tickets'}
                </button>
              )}
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 text-center">
              <p className="text-sm uppercase tracking-wider text-white/60 mb-2">{t?.('display.totalPot') || 'Total Pot'}</p>
              <p className="text-5xl font-bold">
                {formatCurrencyValue(mainDraw.current_jackpot, { language })}
              </p>
            </div>
          </div>
        </div>
      )
    },
    // Style4 / Game Show: dark theme with slot-machine digits
    'game-show': ({ mainDraw, salesClosed, onLoginClick, t, language }) => {
      if (!mainDraw) return null
      return (
        <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white py-20">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h1 className="text-5xl md:text-6xl font-black tracking-tight mb-6">
              {mainDraw.name || t?.('sectionRegistry.gameShow') || 'Game Show'}
            </h1>
            <div className="text-6xl md:text-8xl font-black text-yellow-400 my-8">
              {formatCurrencyValue(mainDraw.current_jackpot, { language })}
            </div>
            <div className="grid grid-cols-3 gap-6 max-w-lg mx-auto mt-8">
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-2xl font-bold">{mainDraw.tickets_sold || 0}</p>
                <p className="text-xs text-gray-400 uppercase">{t?.('display.ticketsSold') || 'Tickets Sold'}</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-2xl font-bold">{mainDraw.players_count || 0}</p>
                <p className="text-xs text-gray-400 uppercase">{t?.('display.players') || 'Players'}</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-2xl font-bold">
                  {formatCurrencyValue(Math.floor((mainDraw.current_jackpot || 0) / 2), { language })}
                </p>
                <p className="text-xs text-gray-400 uppercase">{t?.('classic5050.toWinner') || 'To Winner'}</p>
              </div>
            </div>
            {!salesClosed && (
              <button
                onClick={onLoginClick}
                className="mt-10 bg-yellow-400 text-gray-900 px-10 py-4 rounded-lg font-black text-lg hover:bg-yellow-300 transition-colors"
              >
                {t?.('gameShow.getTickets') || 'Get Tickets Now'}
              </button>
            )}
          </div>
        </div>
      )
    },
  },
})

// --- Last Draw Info ---
register('last_draw_info', {
  default: ({ mainDraw, t, language }) => {
    if (!mainDraw) return null
    return (
      <div className="py-8 bg-white dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t?.('standard.lastDraw') || 'Last Draw'}
          </h3>
          <div className="text-4xl font-bold text-gray-900 dark:text-white">
            {formatCurrencyValue(mainDraw.current_jackpot || 0, { language })}
          </div>
        </div>
      </div>
    )
  },
})

// --- How It Works ---
register('how_it_works', {
  default: ({ t, mainDraw }) => {
    return (
      <div className="py-12 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-8">
            {t?.('standard.howItWorksTitle') || 'How It Works'}
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 dark:text-blue-300 font-bold">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                {t?.('sectionRegistry.buyTicket') || 'Buy a Ticket'}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                {t?.('sectionRegistry.purchaseOnline') || 'Purchase your tickets online'}
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 dark:text-blue-300 font-bold">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                {t?.('superballBingo.step3Title') || 'Watch the Draw'}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                {t?.('sectionRegistry.watchLiveDraw') || 'Tune in for the live draw'}
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-blue-600 dark:text-blue-300 font-bold">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                {t?.('sectionRegistry.winHalf') || 'Win Half'}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                {t?.('classic5050.feature1Description') || 'Winner takes half the pot'}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  },
  variants: {
    'game-show': ({ t, mainDraw, salesClosed, onLoginClick }) => {
      // Style4 "Quick Picks" variant
      return (
        <div className="py-12 bg-gray-800 text-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-8">
              {t?.('gameShow.quickPicks') || 'Quick Picks'}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 5, 10, 25].map(count => (
                <button
                  key={count}
                  onClick={onLoginClick}
                  disabled={salesClosed}
                  className="relative bg-gray-700 hover:bg-gray-600 rounded-xl p-6 transition-all hover:scale-105 disabled:opacity-50"
                >
                  {count === 10 && (
                    <span className="absolute -top-2 -right-2 bg-yellow-400 text-gray-900 text-xs font-bold px-2 py-1 rounded-full">
                      {t?.('gameShow.bestValue') || 'BEST VALUE'}
                    </span>
                  )}
                  <span className="block text-3xl font-black">{count}</span>
                  <span className="block text-sm text-gray-400 mt-1">
                    {count === 1
                      ? t?.('purchase.ticket') || 'Ticket'
                      : t?.('display.tickets') || 'Tickets'}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )
    },
  },
})

// ---------------------------------------------------------------------------
// Stats + Winners (PremiumPlus specific)
// ---------------------------------------------------------------------------

register('stats_winners', {
  default: ({ mainDraw, editMode, language, t }) => {
    if (!mainDraw) return null
    const stats = [
      { label: t?.('display.totalPot') || 'Total Pot', value: `${formatCurrencyValue(mainDraw.current_jackpot || 0, { language })}`, icon: '💰' },
      { label: t?.('sectionRegistry.winnerPrize') || 'Winner Prize', value: `${formatCurrencyValue(Math.floor((mainDraw.current_jackpot || 0) / 2), { language })}`, icon: '🏆' },
      { label: t?.('sectionRegistry.toCharity') || 'To Charity', value: `${formatCurrencyValue(Math.floor((mainDraw.current_jackpot || 0) / 2), { language })}`, icon: '❤️' },
      { label: t?.('display.ticketsSold') || 'Tickets Sold', value: (mainDraw.tickets_sold || 0).toLocaleString(), icon: '🎟️' },
    ]
    return (
      <div className="py-12 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map(stat => (
              <div key={stat.label} className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 text-center">
                <span className="text-3xl mb-2 block">{stat.icon}</span>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Top Partners (PremiumPlus specific)
// ---------------------------------------------------------------------------

register('top_partners', {
  default: ({ partner, t }) => {
    // Simplified version — full version uses usePartnerData hook
    if (!partner) return null
    return (
      <div className="py-12 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-8">
            {t?.('sectionRegistry.ourPartners') || 'Our Partners'}
          </h2>
          <PartnerBanner partner={partner} />
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Live Draw (Standard layout inline)
// ---------------------------------------------------------------------------

register('live_draw', {
  // Standard: 2-column with image/slides + jackpot/countdown/CTA (matches Classic5050StyleStandard)
  default: ({ mainDraw, slides, salesClosed, onLoginClick, t, language }) => {
    if (!mainDraw) return null
    const drawDateLabel = mainDraw.draw_date
      ? new Date(mainDraw.draw_date).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      : ''
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 md:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          <div>
            {slides && slides.length > 0 ? (
              <RichImageCarousel slides={slides} />
            ) : mainDraw.draw_image_url ? (
              <img
                src={mainDraw.draw_image_url}
                alt={mainDraw.name}
                className="w-full rounded-xl"
              />
            ) : (
              <div className="aspect-video rounded-xl bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 border border-blue-100 dark:border-gray-700 flex items-center justify-center">
                <div className="text-center space-y-2">
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {t?.('sectionRegistry.communityJackpot') || 'Community Powered Jackpot'}
                  </div>
                  <div className="text-4xl font-black text-gray-900 dark:text-white">
                    {formatCurrencyValue(mainDraw.current_jackpot, { language })}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-6">
            {drawDateLabel && (
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold">
                  {t?.('display.nextDraw') || 'Next Draw'}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                  {drawDateLabel}
                </p>
              </div>
            )}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-5">
              <p className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold mb-3">
                {t?.('standard.currentJackpot') || 'Current Jackpot'}
              </p>
              <p className="text-4xl md:text-5xl font-black text-gray-900 dark:text-white">
                {formatCurrencyValue(mainDraw.current_jackpot || 0, { language })}
              </p>
            </div>
            {!salesClosed && (
              <button
                onClick={onLoginClick}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-charity-primary text-white font-semibold rounded-lg shadow hover:bg-charity-primary-dark transition-colors"
              >
                {t?.('standard.buyTickets') || 'Buy Tickets'}
              </button>
            )}
          </div>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Hero Carousel (Classic 50/50 Style1)
// ---------------------------------------------------------------------------

register('hero_carousel', {
  default: ({ t }) => {
    return (
      <div className="py-12 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-center">
        <h1 className="text-4xl font-bold mb-4">{t?.('classic5050.slide1Title') || 'Win Big, Give Back'}</h1>
        <p className="text-lg">{t?.('classic5050.slide1Subtitle') || 'Play weekly for a chance at winning half the pot'}</p>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Countdown Section
// ---------------------------------------------------------------------------

// Wrapper component to properly use the useCountdown hook
function CountdownSection({ mainDraw, t, language }) {
  const { timeLeft } = useCountdown(mainDraw)
  
  if (!mainDraw?.draw_date) return null
  
  return (
    <div className="py-8 bg-white dark:bg-gray-800">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t?.('display.timeUntilDraw') || 'Time Until Draw'}
        </h3>
        <CountdownTimerStyles
          timeLeft={timeLeft}
          shouldAnimate={true}
          style="blocks"
        />
      </div>
    </div>
  )
}

register('countdown', {
  default: (props) => <CountdownSection {...props} />,
})

// ---------------------------------------------------------------------------
// Draw Info Section
// ---------------------------------------------------------------------------

register('draw_info', {
  default: ({ mainDraw, t, language }) => {
    if (!mainDraw) return null
    return (
      <div className="py-8 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t?.('sectionRegistry.drawInformation') || 'Draw Information'}
          </h3>
          <div className="space-y-2 text-gray-700 dark:text-gray-300">
            <p><strong>{t?.('home.drawDate') || 'Draw Date'}:</strong> {mainDraw.draw_date || t?.('progressive.tba') || 'TBD'}</p>
            <p><strong>{t?.('display.ticketsSold') || 'Tickets Sold'}:</strong> {mainDraw.tickets_sold?.toLocaleString() || '0'}</p>
          </div>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Chase the Ace — Cards Remaining
// ---------------------------------------------------------------------------

register('cards_remaining', {
  default: ({ mainDraw, t, language }) => {
    if (!mainDraw) return null
    const cardsRemaining = mainDraw.cards_remaining ?? 52
    return (
      <div className="py-8 bg-white dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            {t?.('display.cardsRemaining') || 'Cards Remaining'}
          </h3>
          <div className="text-6xl font-black text-blue-600 dark:text-blue-400">
            {cardsRemaining} <span className="text-3xl text-gray-500">/ 52</span>
          </div>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Chase the Ace — Rollover Timeline
// ---------------------------------------------------------------------------

register('rollover_timeline', {
  default: ({ mainDraw, t, language }) => {
    return (
      <div className="py-8 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t?.('sectionRegistry.rolloverHistory') || 'Rollover History'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">{t?.('sectionRegistry.rolloverPlaceholder') || 'Rollover data will be displayed here.'}</p>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Chase the Ace — Deck Display
// ---------------------------------------------------------------------------

register('deck_display', {
  default: ({ mainDraw, t, language }) => {
    return (
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t?.('sectionRegistry.remainingCards') || 'Remaining Cards'}
          </h3>
          <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
            {Array.from({ length: mainDraw?.cards_remaining || 52 }, (_, i) => (
              <div key={i} className="aspect-[2/3] bg-blue-600 rounded-lg shadow-md"></div>
            ))}
          </div>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Chase the Ace — Jackpot Summary
// ---------------------------------------------------------------------------

register('jackpot_summary', {
  default: ({ mainDraw, t, language }) => {
    if (!mainDraw) return null
    const jackpot = mainDraw.current_jackpot || 0
    const weeklyPrize = mainDraw.weekly_prize || mainDraw.secondary_prize || 0
    return (
      <div className="py-8 bg-gradient-to-br from-emerald-500 to-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-lg uppercase tracking-wider mb-4">
            {t?.('sectionRegistry.prizes') || 'Prizes'}
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm opacity-80 mb-2">{t?.('display.jackpot') || 'Jackpot'}</p>
              <p className="text-5xl font-black">{formatCurrencyValue(jackpot, { language })}</p>
            </div>
            <div>
              <p className="text-sm opacity-80 mb-2">{t?.('sectionRegistry.weeklyPrize') || 'Weekly Prize'}</p>
              <p className="text-5xl font-black">{formatCurrencyValue(weeklyPrize, { language })}</p>
            </div>
          </div>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Elimination Draw — Tracker
// ---------------------------------------------------------------------------

register('elimination_tracker', {
  default: ({ mainDraw, t, language }) => {
    return (
      <div className="py-8 bg-white dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t?.('sectionRegistry.eliminationTracker') || 'Elimination Tracker'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">{t?.('sectionRegistry.trackerPlaceholder') || 'Tracker will be displayed here.'}</p>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Elimination Draw — Recent Eliminations
// ---------------------------------------------------------------------------

register('recent_eliminations', {
  default: ({ t }) => {
    return (
      <div className="py-8 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t?.('sectionRegistry.recentEliminations') || 'Recent Eliminations'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">{t?.('sectionRegistry.eliminationsPlaceholder') || 'Recent eliminations will be listed here.'}</p>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Prize Draw — Gallery
// ---------------------------------------------------------------------------

register('prize_gallery', {
  default: ({ t }) => {
    return (
      <div className="py-12 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-8">
            {t?.('sectionRegistry.prizeGallery') || 'Prize Gallery'}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-100 dark:bg-gray-700 rounded-xl p-6 text-center">
                <div className="aspect-square bg-gray-200 dark:bg-gray-600 rounded-lg mb-4"></div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{t?.('elimination.prize') || 'Prize'} {i}</h3>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Prize Draw — Summary
// ---------------------------------------------------------------------------

register('prize_summary', {
  default: ({ mainDraw, t, language }) => {
    return (
      <div className="py-8 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-lg uppercase tracking-wider mb-4">
            {t?.('sectionRegistry.prizeSummary') || 'Prize Summary'}
          </h3>
          <p className="text-3xl font-bold">
            {mainDraw?.total_tickets_sold?.toLocaleString() || '0'} {t?.('display.ticketsSold') || 'Tickets Sold'}
          </p>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Superball Bingo — Ball Tracker
// ---------------------------------------------------------------------------

register('ball_tracker', {
  default: ({ t }) => {
    return (
      <div className="py-8 bg-white dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t?.('display.ballTracker') || 'Ball Tracker'}
          </h3>
          <div className="grid grid-cols-10 gap-2">
            {Array.from({ length: 75 }, (_, i) => (
              <div key={i} className="aspect-square bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center text-xs font-bold">
                {i + 1}
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Superball Bingo — Session Info
// ---------------------------------------------------------------------------

register('session_info', {
  default: ({ mainDraw, t, language }) => {
    return (
      <div className="py-8 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-4xl mx-auto px-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {t?.('sectionRegistry.sessionInformation') || 'Session Information'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">{t?.('sectionRegistry.sessionPlaceholder') || 'Session details will be displayed here.'}</p>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Superball Bingo — Live Board Display
// ---------------------------------------------------------------------------

register('live_board_display', {
  default: ({ t }) => {
    return (
      <div className="py-8 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 text-center">
            {t?.('sectionRegistry.liveBoard') || 'Live Board'}
          </h3>
          <div className="aspect-video bg-gray-200 dark:bg-gray-700 rounded-xl flex items-center justify-center">
            <span className="text-gray-500">{t?.('sectionRegistry.liveBoardPlaceholder') || 'Live board display'}</span>
          </div>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Square Game — Grid (uses SquareBoardPreview)
// ---------------------------------------------------------------------------

register('square_grid', {
  default: (props) => <SquareGridSection {...props} />,
})

// ---------------------------------------------------------------------------
// Square Game — Charity Hero (CenterSquareCharity component)
// ---------------------------------------------------------------------------

register('charity_hero', {
  default: ({ t }) => {
    return (
      <div className="py-12 bg-gradient-to-br from-emerald-600 to-green-600 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            {t?.('classic5050.charityBannerTitle') || 'Supporting Our Community'}
          </h2>
          <p className="text-lg opacity-90">
            {t?.('sectionRegistry.squareSupportsPrograms') || 'Every square supports local programs'}
          </p>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Charity Info Card (Split Pot display)
// ---------------------------------------------------------------------------

register('charity_info_card', {
  default: ({ mainDraw }) => {
    return (
      <div className="py-8 bg-white dark:bg-gray-800">
        <div className="max-w-md mx-auto px-4">
          <SplitPotCharity jackpot={mainDraw?.current_jackpot || 0} />
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Jackpot Display (Progressive specific)
// ---------------------------------------------------------------------------

register('jackpot_display', {
  default: ({ mainDraw, t, language }) => {
    if (!mainDraw) return null
    return (
      <div className="py-12 bg-gradient-to-br from-yellow-400 to-orange-500 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-lg uppercase tracking-wider mb-4">
            {t?.('progressive.jackpot') || 'Progressive Jackpot'}
          </h2>
          <div className="text-7xl font-black">
            {formatCurrencyValue(mainDraw.current_jackpot || 0, { language })}
          </div>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Jackpot Card (Progressive card with stats)
// ---------------------------------------------------------------------------

register('jackpot_card', {
  default: ({ mainDraw, t, language }) => {
    if (!mainDraw) return null
    return (
      <div className="py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-purple-600 to-blue-600 text-white rounded-2xl p-8 shadow-2xl">
            <h3 className="text-lg uppercase tracking-wider mb-4 text-center">
              {t?.('progressive.currentJackpot') || 'Current Jackpot'}
            </h3>
            <p className="text-6xl font-black text-center">
              {formatCurrencyValue(mainDraw.current_jackpot || 0, { language })}
            </p>
          </div>
        </div>
      </div>
    )
  },
})

// ---------------------------------------------------------------------------
// Quick Stats
// ---------------------------------------------------------------------------

register('quick_stats', {
  default: ({ mainDraw, t, language }) => {
    if (!mainDraw) return null
    return (
      <div className="py-8 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white dark:bg-gray-700 rounded-lg p-6 text-center">
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {mainDraw.tickets_sold?.toLocaleString() || '0'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {t?.('display.ticketsSold') || 'Tickets Sold'}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-700 rounded-lg p-6 text-center">
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {formatCurrencyValue(mainDraw.current_jackpot || 0, { language })}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {t?.('display.jackpot') || 'Jackpot'}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-700 rounded-lg p-6 text-center">
              <p className="text-3xl font-bold text-gray-900 dark:text-white">
                {mainDraw.players_count?.toLocaleString() || '0'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {t?.('display.players') || 'Players'}
              </p>
            </div>
          </div>
        </div>
      </div>
    )
  },
})

export default registry
